import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState, type ComponentProps } from 'react';
import styled from 'styled-components';

import type {
  SeasonEligibility,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { DisplayIfVisible } from '@sorare/core/src/atoms/layout/DisplayIfVisible';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
  ClickMarketHomeSection_Section,
  type ClickMarketHomeSection,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { Filters } from '@sorare/marketplace/src/components/market-home/Filters';
import { FlashSalesBanner } from '@sorare/marketplace/src/components/market-home/FlashSalesBanner';
import { FriendsRecentPurchases } from '@sorare/marketplace/src/components/market-home/FriendsRecentPurchases';
import { MarketHeroCarousel } from '@sorare/marketplace/src/components/market-home/MarketHeroCarousel';
import { MarketShopTabs } from '@sorare/marketplace/src/components/market-home/MarketShopTabs';
import { TrendingAuctions } from '@sorare/marketplace/src/components/market-home/TrendingAuctions';
import {
  PlayerMarketDrawer,
  PlayerMarketDrawerVariant,
} from '@sorare/marketplace/src/components/player/PlayerMarketDrawer';

import { Collections } from '#src/components/market-home/Collections/index.tsx';

import type {
  MarketHomePageQuery,
  MarketHomePageQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  container-type: inline-size;
`;

const Layout = styled(Horizontal)`
  gap: 0;
`;
const Main = styled(Vertical)`
  max-width: 100%;
  padding: var(--double-unit) 0 var(--quadruple-unit);
  @media ${laptopAndAbove} {
    padding: calc(var(--unit) * 6) 0;
  }
`;

const MARKET_HOME_PAGE_QUERY = gql`
  query MarketHomePageQuery(
    $sport: Sport!
    $so5CompetitionSlug: String!
    $rarity: Rarity!
  ) {
    forYou {
      friendsRecentPurchases(
        so5CompetitionSlug: $so5CompetitionSlug
        sport: $sport
        rarity: $rarity
      ) {
        id
        ...FriendsRecentPurchases_tokenOwner
      }
    }
  }
  ${FriendsRecentPurchases.fragments.tokenOwner}
` as TypedDocumentNode<MarketHomePageQuery, MarketHomePageQueryVariables>;

export const MarketHomePage = ({ sport }: { sport: Sport }) => {
  const [marketPlayerSlug, setMarketPlayerSlug] = useState<string | null>(null);
  const [filters, setFilters] =
    useState<ComponentProps<typeof PlayerMarketDrawer>['filters']>(undefined);

  const isLaptopAndAbove = useIsLaptop();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { lifecycle } = useLifecycle();

  const rarity = (() => {
    if (lifecycle?.marketHomeFilters?.[sport]?.rarity === Rarity.common) {
      return Rarity.limited;
    }
    return lifecycle?.marketHomeFilters?.[sport]?.rarity || Rarity.limited;
  })();

  const lifecycleSo5CompetitionSlug =
    lifecycle?.marketHomeFilters?.[sport]?.competitionSlug || '';

  const so5CompetitionSlug =
    lifecycleSo5CompetitionSlug !== 'DEFAULT'
      ? lifecycleSo5CompetitionSlug
      : '';
  const { data } = useQuery(MARKET_HOME_PAGE_QUERY, {
    variables: {
      sport,
      so5CompetitionSlug,
      rarity,
    },
  });

  const track = useEvents();

  const createTrackMarketHomeSection = (
    origin: ClickMarketHomeSection_Section
  ) => {
    return (
      props: Omit<
        ClickMarketHomeSection,
        'origin' | 'sport' | 'scarcity' | 'so5CompetitionSlug'
      >
    ) => {
      track('Click Market Home Section', {
        so5CompetitionSlug,
        scarcity: rarity,
        sport,
        origin,
        ...props,
      });
    };
  };
  if (rarity === Rarity.custom_series) return null;

  const onMarketClick = (slug: string, seasonality: SeasonEligibility) => {
    setMarketPlayerSlug(slug);
    setFilters({
      seasonality,
      rarity,
    });
  };

  return (
    <Root>
      <Filters sport={sport} />
      <MarketHeroCarousel
        rarity={rarity}
        sport={sport}
        topBar={
          isTabletAndAbove ? undefined : (
            <MarketShopTabs sport={sport} rarity={rarity} />
          )
        }
      />
      <>
        <Layout>
          <Main gap={isLaptopAndAbove ? 6 : 5}>
            <FlashSalesBanner sport={sport} />
            <FriendsRecentPurchases
              onMarketClick={onMarketClick}
              tokenOwners={data?.forYou?.friendsRecentPurchases || []}
              track={createTrackMarketHomeSection(
                ClickMarketHomeSection_Section.FRIENDS_RECENT_PURCHASES
              )}
            />
            <TrendingAuctions
              sport={sport}
              track={createTrackMarketHomeSection(
                ClickMarketHomeSection_Section.TRENDING_AUCTIONS
              )}
            />
            <DisplayIfVisible>
              <Collections
                so5CompetitionSlug={so5CompetitionSlug}
                sport={sport}
                rarity={rarity}
                track={createTrackMarketHomeSection(
                  ClickMarketHomeSection_Section.COLLECTION
                )}
              />
            </DisplayIfVisible>
          </Main>
        </Layout>
      </>
      <PlayerMarketDrawer
        onClose={() => setMarketPlayerSlug(null)}
        playerSlug={marketPlayerSlug}
        filters={filters}
        sport={sport}
        canUseConversionCredit
        origin={{
          instantBuy: ClickInstantBuy_Flow.MARKETPLACE_HOME,
          addToCart: ClickAddToCart_Origin.MARKETPLACE_HOME,
        }}
        variant={PlayerMarketDrawerVariant.MARKET_HOME}
      />
    </Root>
  );
};
